/**
 * Utilities for editor forms that manage arrays of items.
 *
 * Using array index as a React `key` breaks reconciliation when items are
 * added/removed/reordered (inputs lose focus, state can bleed across rows).
 * These helpers attach a stable, opaque `_uid` to every array item on load
 * and on add — used only client-side, stripped before saving to the API.
 */

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Return a new item with a stable client-side `_uid`. */
export const withUid = (item) => {
  if (item && typeof item === 'object') {
    return item._uid ? item : { ...item, _uid: uid() };
  }
  // Primitive (string) — wrap so we can still key it reliably.
  return { _uid: uid(), value: item };
};

/** Ensure every item in an array has a `_uid`. */
export const ensureUids = (arr = []) => arr.map(withUid);

/** Strip `_uid` from every item before sending to the API.
 *  If the item was a wrapped primitive (only `_uid` + `value`), unwrap it back. */
export const stripUids = (arr = []) => arr.map(({ _uid: _drop, ...rest }) => {
  const keys = Object.keys(rest);
  if (keys.length === 1 && keys[0] === 'value') return rest.value;
  return rest;
});

/** Fresh uid for a brand new blank row. */
export const newUid = uid;
